/*
 * Engraving bonded-pair guard — runs GLOBALLY on every page (Dawn).
 *
 * The camera and its engraving service line are a bonded pair:
 *   • FORWARD  — remove the camera  → its engraving line is auto-removed.
 *   • REVERSE  — remove the engraving line → the camera it belongs to is auto-removed.
 * So neither ever sits alone in the cart, no matter where the customer removes it
 * from (cart drawer, cart page, or any other page).
 *
 * Pairing markers (stamped at add-to-cart time by mini12camera-product.js):
 *   • engraving line : properties["_for_variant"]  = the camera's variant id
 *   • camera line    : properties["_has_engraving"] = "1"
 *
 * Config is injected by layout/theme.liquid as #engraving-config:
 *   { "engravingVariantId": <int|null>, "eligibleProductIds": [<int>, ...] }
 */
(function () {
  'use strict';

  var configEl = document.getElementById('engraving-config');
  if (!configEl) return;

  var config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch (e) {
    return;
  }

  var engId = parseInt(config.engravingVariantId, 10);
  if (!engId) return; // feature off / not configured → guard no-ops

  var eligible = new Set((config.eligibleProductIds || []).map(function (id) { return parseInt(id, 10); }));

  var DEBOUNCE = 120;         // was 350 — reconcile faster
  var syncing = false;
  var pending = false;
  var timer = null;
  var lastItemCount = null;   // to tell an add (count up) from a removal (count down)
  var hadPair = false;        // cart currently holds a bonded pair → a removal could orphan it

  function getCartEl() {
    return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  }

  // Block / unblock every way to reach checkout while the pair is out of sync,
  // so the customer can't check out with a single orphaned line during the gap.
  // Only ever re-enables buttons WE disabled (dataset flag), never a legitimately
  // disabled one (e.g. empty cart).
  function setCheckoutGate(disabled) {
    var sel = '[name="checkout"], .cart__checkout-button, .shopify-payment-button, .cart__dynamic-checkout-buttons, .additional-checkout-buttons';
    document.querySelectorAll(sel).forEach(function (el) {
      if (disabled) {
        el.dataset.engGated = '1';
        el.setAttribute('aria-disabled', 'true');
        if ('disabled' in el) el.disabled = true;
        el.style.pointerEvents = 'none';
        el.style.opacity = '0.5';
      } else if (el.dataset.engGated) {
        delete el.dataset.engGated;
        el.removeAttribute('aria-disabled');
        if ('disabled' in el) el.disabled = false;
        el.style.pointerEvents = '';
        el.style.opacity = '';
      }
    });
  }

  function schedule() {
    // Cart just mutated while a bonded pair was present → an orphan may be seconds
    // from existing. Gate checkout immediately (same tick as the drawer re-render,
    // long before a human can click), then run() lifts it once reconciled.
    if (hadPair) setCheckoutGate(true);
    clearTimeout(timer);
    timer = setTimeout(run, DEBOUNCE);
  }

  function run() {
    if (syncing) { pending = true; return; }

    syncing = true;
    fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var items = cart.items || [];
        var count = (typeof cart.item_count === 'number') ? cart.item_count : items.length;
        var isRemoval = (lastItemCount !== null && count < lastItemCount);
        lastItemCount = count;

        // Add-grace: only defer for a fresh ADD (so we don't remove a pair that is
        // still mid-add). On a REMOVAL we must act at once — otherwise the orphan
        // lingers and the customer could check out with a single line.
        var grace = window.__engraveAddGrace || 0;
        if (!isRemoval && Date.now() < grace) {
          setCheckoutGate(false); // an add mid-grace, not a removal → don't block checkout
          clearTimeout(timer);
          timer = setTimeout(run, (grace - Date.now()) + 200);
          return null;
        }

        var variantIdsInCart = new Set(items.map(function (it) { return it.variant_id; }));
        var productIdsInCart = new Set(items.map(function (it) { return it.product_id; }));

        // Variant ids that currently have an engraving line linked to them.
        var engForVariants = new Set();
        items.forEach(function (it) {
          if (it.variant_id !== engId) return;
          var forVariant = it.properties && (it.properties._for_variant || it.properties['_for_variant']);
          if (forVariant) engForVariants.add(parseInt(forVariant, 10));
        });

        var toRemove = [];
        var pairPresent = false;

        items.forEach(function (it) {
          var props = it.properties || {};

          if (it.variant_id === engId) {
            // FORWARD — is this engraving line's camera gone?
            var forVariant = props._for_variant || props['_for_variant'];
            var orphan;
            if (forVariant) {
              orphan = !variantIdsInCart.has(parseInt(forVariant, 10));
              if (!orphan) pairPresent = true;
            } else if (eligible.size > 0) {
              // Legacy lines without _for_variant: orphaned only when NONE of the
              // eligible products remain in the cart.
              orphan = ![].concat(Array.from(productIdsInCart)).some(function (pid) { return eligible.has(pid); });
            } else {
              orphan = false; // can't determine safely → keep
            }
            if (orphan) toRemove.push(it);
          } else {
            // REVERSE — a camera marked _has_engraving whose engraving line is gone.
            // Only ever fires on explicitly-marked lines, so legacy carts are untouched.
            var marked = props._has_engraving || props['_has_engraving'];
            if (marked) {
              if (engForVariants.has(it.variant_id)) pairPresent = true;
              else toRemove.push(it);
            }
          }
        });

        // Remember whether a bonded pair is still in the cart, so schedule() knows
        // to gate checkout the instant the next mutation happens.
        hadPair = pairPresent;

        if (!toRemove.length) { setCheckoutGate(false); return null; }

        // Orphan(s) found → keep checkout blocked until the partner(s) are gone.
        setCheckoutGate(true);

        var cartEl = getCartEl();
        var sections = (cartEl && typeof cartEl.getSectionsToRender === 'function')
          ? cartEl.getSectionsToRender().map(function (s) { return s.id; })
          : [];

        // Remove sequentially (line keys stay valid; quantity 0 = remove).
        return toRemove.reduce(function (chain, line) {
          return chain.then(function () {
            var body = { id: line.key, quantity: 0 };
            if (sections.length) { body.sections = sections; body.sections_url = window.location.pathname; }
            return fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
              body: JSON.stringify(body)
            }).then(function (r) { return r.json(); });
          });
        }, Promise.resolve()).then(function (lastResult) {
          if (lastResult) {
            if (typeof lastResult.item_count === 'number') lastItemCount = lastResult.item_count;
            if (cartEl) {
              if (lastResult.item_count === 0) cartEl.classList.add('is-empty');
              if (typeof cartEl.renderContents === 'function') cartEl.renderContents(lastResult);
            }
          }
          hadPair = false;         // partner removed → nothing left to guard (recomputed next run)
          setCheckoutGate(false);  // pair reconciled → let checkout through
          return lastResult;
        });
      })
      .catch(function () { /* network/parse errors → leave cart as-is (gate stays until next run) */ })
      .finally(function () {
        syncing = false;
        if (pending) { pending = false; schedule(); }
      });
  }

  // Dawn pub/sub (fires on the theme's own cart mutations)
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, schedule);
  }

  // Fallback: observe the cart drawer/notification for any DOM re-render (covers
  // removals that don't publish through pub/sub, and other pages).
  function attachObserver() {
    var el = getCartEl();
    if (el) new MutationObserver(schedule).observe(el, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attachObserver);
  } else {
    attachObserver();
  }

  // Initial reconciliation on load (e.g. landing on the cart page).
  run();
})();
